import React, { FC, useRef, useState, useEffect } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CompaniesBasicsListItemDto } from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { CompanySelectButton } from "./SidebarCompanySelect.styled";
import CompanySelectPopover from "./components/CompanySelectPopover";

import { useUserStore } from "@store/user";
import { Warn } from "@helpers";
import { useGetAssignedCompanies } from "@api";
import { useAuth } from "@hooks/useAuth";
import { Box, Typography } from "@components/base";

const SidebarCompanySelect: FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<CompaniesBasicsListItemDto>();
  const anchorEl = useRef<HTMLButtonElement>(null);
  const { user } = useUserStore();
  const { getUser, refreshToken } = useAuth();
  const { assignedCompanies, isPendingGetAssignedCompanies } =
    useGetAssignedCompanies();
  const queryClient = useQueryClient();

  useEffect(() => {
    const activeCompanyId =
      user?.activeCompanyId || localStorage.getItem("activeCompanyId");

    if (activeCompanyId && assignedCompanies) {
      setSelectedCompany(assignedCompanies.find((c) => c.id === activeCompanyId));
    }
  }, [user, assignedCompanies]);

  const handlePopoverOpen = (): void => {
    setOpen(!open);
  };

  const handleCompanySelect = async (
    company: CompaniesBasicsListItemDto,
  ): Promise<void> => {
    const token = localStorage.getItem("refresh_token");

    if (token) {
      await refreshToken(company.id);
    } else {
      Warn("No refresh token found!");
    }

    queryClient.removeQueries();

    await getUser();

    navigate("/");

    setOpen(false);
  };

  return (
    <Box mb={2}>
      <CompanySelectButton
        fullWidth
        variant="contained"
        endIcon={<ArrowDropDownIcon fontSize="large" />}
        ref={anchorEl}
        onClick={handlePopoverOpen}
      >
        <Typography>
          {isPendingGetAssignedCompanies ? "Loading..." : selectedCompany?.name}
        </Typography>
      </CompanySelectButton>
      <CompanySelectPopover
        open={open}
        onClose={handlePopoverOpen}
        anchorEl={anchorEl.current}
        onSelectCompany={(company) => handleCompanySelect(company)}
      />
    </Box>
  );
};

export default SidebarCompanySelect;
